<template>
  <div class="hq-address-form">
    <AkParagraph
      weight="bold"
      size="sm"
    >
      {{ $t('retailer.germanBusinessIdentifier.hqAddress.title') }}
    </AkParagraph>

    <AkInput
      v-model="street"
      :label="$t('retailer.germanBusinessIdentifier.hqAddress.street')"
      :has-error="touched.street && !street"
      :errors="touched.street && !street ? [$t('This field is required')] : []"
      required
      size="medium"
      data-testid="hq-street-input"
      @blur="touched.street = true"
    />

    <AkInput
      v-model="postcode"
      :label="$t('retailer.germanBusinessIdentifier.hqAddress.postcode')"
      :has-error="touched.postcode && !postcode"
      :errors="touched.postcode && !postcode ? [$t('This field is required')] : []"
      required
      size="medium"
      data-testid="hq-postcode-input"
      @blur="touched.postcode = true"
    />

    <AkInput
      v-model="city"
      :label="$t('retailer.germanBusinessIdentifier.hqAddress.city')"
      :has-error="touched.city && !city"
      :errors="touched.city && !city ? [$t('This field is required')] : []"
      required
      size="medium"
      data-testid="hq-city-input"
      @blur="touched.city = true"
    />

    <AkSelect
      v-model="selectedCountry"
      :label="$t('retailer.germanBusinessIdentifier.hqAddress.country')"
      :options="countryOptions"
      data-testid="hq-country-select"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { AkInput, AkParagraph, AkSelect } from '@ankorstore/design-system';
import type { HqAddress } from '../types/german-bi.types';

const props = withDefaults(
  defineProps<{
    initialAddress?: HqAddress | null;
  }>(),
  {
    initialAddress: null,
  }
);

const emit = defineEmits<{
  update: [address: HqAddress | null];
}>();

const store = useStore();

const street = ref(props.initialAddress?.street || '');
const postcode = ref(props.initialAddress?.postalCode || '');
const city = ref(props.initialAddress?.city || '');
const selectedCountry = ref(props.initialAddress?.countryIsoCode || 'DE');
const touched = reactive({ street: false, postcode: false, city: false });

onMounted(() => {
  store.dispatch('global/countries/fetchCountries');
});

const countries = computed(() => store.getters['global/countries/getAlphaOrderedCountries'] || []);

const countryOptions = computed(() =>
  countries.value.map((c: { name: string; iso_code: string }) => ({
    label: c.name,
    value: c.iso_code,
  }))
);

const countryMap = computed(() => {
  const map: Record<string, { id: number; iso_code: string }> = {};
  for (const c of countries.value) {
    map[c.iso_code] = c;
  }
  return map;
});

const resolvedAddress = computed<HqAddress | null>(() => {
  if (!street.value || !postcode.value || !city.value || !selectedCountry.value) {
    return null;
  }

  const country = countryMap.value[selectedCountry.value];
  if (!country) {
    return null;
  }

  return {
    street: street.value,
    city: city.value,
    postalCode: postcode.value,
    countryId: country.id,
    countryIsoCode: country.iso_code,
  };
});

watch(
  resolvedAddress,
  (address) => {
    emit('update', address);
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.hq-address-form {
  @apply ds-flex ds-flex-col ds-gap-3 ds-mt-4;
}
</style>
